'use client';

import dayjs from 'dayjs';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { timezone } from '../utils/timeZone';

const getTzValue = (tz) => {
  if (tz !== undefined && tz !== null && !isNaN(tz)) return Number(tz);
  const defaultTz = typeof timezone === 'function' ? timezone() : Number(timezone);
  return !isNaN(defaultTz) ? defaultTz : Math.abs(new Date().getTimezoneOffset() / 60);
};

const CACHE_TTL_MS = 10 * 60 * 1000;

export const dashboardCache = {
  get: (key) => {
    try {
      const cachedItem = localStorage.getItem(key);
      if (!cachedItem) return null;

      const parsed = JSON.parse(cachedItem);
      if (Date.now() < parsed.expired_at) {
        return parsed.data;
      } else {
        localStorage.removeItem(key);
        return null;
      }
    } catch (error) {
      console.error('Failed to read from localStorage cache:', error);
      return null;
    }
  },
  set: (key, data) => {
    try {
      const cacheData = {
        data,
        expired_at: Date.now() + CACHE_TTL_MS
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Failed to write to localStorage cache:', error);
    }
  },
  clear: () => {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith('revenue_') ||
          key.startsWith('asset_') ||
          key.startsWith('report_trip_') ||
          key.startsWith('periodeRange_') ||
          key.startsWith('cache_')
        ) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Failed to clear localStorage cache:', error);
    }
  }
};

const formatIsoClean = (dateStr) => {
  if (!dateStr) return '';
  const cleanStr = dateStr.split('.')[0];
  if (cleanStr.endsWith('Z')) return cleanStr;
  return cleanStr + 'Z';
};
export const fetchRetase = async (params) => {
  try {
    const cleanParams = {
      vehicleId: params.vehicleId,
      companyId: params.companyId,
      tz: getTzValue(params.tz),
      startTime: formatIsoClean(params.startTime),
      endTime: formatIsoClean(params.endTime),
      isRouteSort: true
    };

    const response = await fleetApi.get('/retase', {
      params: cleanParams,
      signal: params.signal
    });
    return response || [];
  } catch (error) {
    if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
      console.error('Failed to fetch retase:', error);
    }
    throw error;
  }
};

export const fetchRetaseDetail = async (params) => {
  try {
    // Parameter URL
    const cleanParams = {
      companyId: params.companyId,
      tz: getTzValue(params.tz),
      vehicleId: params.vehicleId,
      routeTripId: params.routeTripId,
      startTime: formatIsoClean(params.startTime),
      endTime: formatIsoClean(params.endTime)
    };

    if (params.nextRouteTime) {
      cleanParams.nextRouteTime = formatIsoClean(params.nextRouteTime);
    }

    const response = await fleetApi.get('/retase-detail', {
      params: cleanParams,
      signal: params.signal
    });

    return response;
  } catch (error) {
    if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
      console.error('Failed to fetch retase detail:', error);
    }
    throw error;
  }
};

export const fetchVisitationRetase = async (params) => {
  try {
    if (!params || !params.startTime || !params.endTime) return [];

    const startIso = dayjs(params.startTime).isValid()
      ? dayjs(params.startTime).format('YYYY-MM-DDTHH:mm:ss[Z]')
      : new Date(params.startTime).toISOString().replace(/\.\d{3}Z$/, 'Z');
    const endIso = dayjs(params.endTime).isValid()
      ? dayjs(params.endTime).format('YYYY-MM-DDTHH:mm:ss[Z]')
      : new Date(params.endTime).toISOString().replace(/\.\d{3}Z$/, 'Z');

    const fb = new FilterBuilder()
      .setPaging(0, 1000)
      .equal('company_id', String(params.companyId || ''))
      .equal('vehicle_id', String(params.vehicleId || ''))
      .range('wp_time', startIso, endIso);

    const payload = fb.build();

    const urlParams = {
      tz: getTzValue(params.tz),
      stop_location_by_off: 1,
      stop_location_by_idle: 1,
      isAllVisit: true
    };

    const response = await fleetApi.post('/report/trip-visitation/data-visit/search', payload, {
      params: urlParams,
      signal: params.signal
    });

    return response.data;
  } catch (error) {
    if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
      console.error('Failed to fetch visitation:', error);
    }
    throw error;
  }
};
